/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-10 17:03:24                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-10 17:34:05                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * CompInfosListItem: Display compinfo item
    * getCompInfosChoices: Get compinfos choices
*/

/* Imports */
import { IconButton, Tooltip, Typography } from "@material-tailwind/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PencilIcon } from "@heroicons/react/24/solid";
import { t } from "i18next";
/***/

/**
* CompInfosListItem: Display compinfo item
* @parma compInfo: CompInfo to display
* @param onClick: Function to call when item is clicked
* @param isLast: If the item is the last one 
* @param isReordering: If we are in reordering mode
* @param index: Optional index position (for display)
*/
export default function CompInfosListItem({
  compInfo,
  onClick,
  onChange,
  isLast = false,
  isReordering = false,
  index
}: {
  compInfo: EasyTech.CompInfo,
  onClick?: (compInfo: EasyTech.CompInfo) => void,
  onChange: () => void,
  isLast?: boolean,
  isReordering?: boolean,
  index?: number
}) {  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({
    id: compInfo.id,
    disabled: !isReordering
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  /**
  * getCompInfosChoices: Get compinfos choices
  * @param choices: CompInfo choices 
  * @return - CompInfo choices list as string
  */
  const getCompInfosChoices = (choices?: EasyTech.CompInfoChoice[]) => {
    if (choices) return choices.map(choice => choice.label).join(", ");
    else return "-";
  };
  /***/

  const editCompInfo = (compInfo: EasyTech.CompInfo) => {
    if (onClick)
      onClick(compInfo);
  };

  return (
    <tr 
      ref={setNodeRef} 
      style={style}
      className={`page-list-item ${isLast && "last"} ${isReordering ? "cursor-move relative group" : ""}`}
      {...attributes}
      {...listeners}
    >
      <td>
        <div className="flex items-center">
          {isReordering && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-400">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              </svg>
            </div>
          )}
          <Typography variant="small" color="blue-gray">
            {index || (compInfo.order !== undefined ? compInfo.order : "-")}
          </Typography>
        </div>
      </td>

      {isReordering &&
        <td>
          <Typography variant="small" color="blue-gray">
            {compInfo.usage || "-"}
          </Typography>
        </td>
      }
      
      <td>
        <Tooltip content={compInfo.label}>
          <Typography variant="small" color="blue-gray">
            {compInfo.label}
          </Typography>
        </Tooltip>
      </td>

      <td>
      <Tooltip content={compInfo.type}>
          <Typography variant="small" color="blue-gray">
            {compInfo.type}
          </Typography>
        </Tooltip>
      </td>

      <td>
        <Tooltip content={getCompInfosChoices(compInfo.choices)}>
          <Typography variant="small" color="blue-gray">
            {getCompInfosChoices(compInfo.choices)}
          </Typography>
        </Tooltip>
      </td>

      <td>
        <Typography variant="small" color="blue-gray">
          {compInfo.constraints === "REQUIRED" ? "Yes" : "No"}
        </Typography>
      </td>

      <td>
        <Typography variant="small" color="blue-gray">
          {compInfo.enabled ? "Yes" : "No"}
        </Typography>
      </td>

      {/*
        * Read-only: the toggle itself lives in the edit drawer. This column exists so a super admin
        * can see at a glance which fields reach customer invoices and confirm afterwards that none
        * are left — checking that one drawer at a time across the whole list is how a review pass
        * quietly never gets finished.
        */}
      <td>
        <Typography variant="small" color="blue-gray">
          {compInfo.sendToInvoice ? t("common:yes") : t("common:no")}
        </Typography>
      </td>

      {!isReordering &&
        <td className="flex">
          <IconButton onClick={() => editCompInfo(compInfo)} 
              variant="text"
              className={"icon-btn"} 
              size="sm">
            <PencilIcon className="w-[16px]" />
          </IconButton>
        </td>
      }
    </tr>
  );
}
/***/
